import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { Activos, ActivosPagingRes } from '@/shared/interfaces/erp/activos';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';


const { get, post, put, remove } = erpApi();

///* tanSctack query
export const usefetchActivos = (params?: GetActivosParams) => {
  return useQuery({
    queryKey: ['Activos', ...Object.values(params || {})],
    queryFn: async () => {
      return getActivos(params);
    },
  });
};

export const useGetActivo = (id: string) => {
  return useQuery({
    queryKey: ['Activos', id],
    queryFn: async () => {
      return getActivo(id);
    },
  });
};
export const useCreateActivos = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createActivos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Activos'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Activo creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateActivos = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateActivos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Activos'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Activo actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteActivos = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: async (id: string) => removeActivos(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Activos'] });
      setConfirmDialogIsOpen(false);
    },
    onError: (error) => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetActivosParams = {
  page?: number,
  page_size?: number,
  id_activo?: number;
  codigo_barras?: string;
  color?: string;
  tipo_calculo_activos?: string;
  serie?: string;
  estado_fisico?: string;
  estado_constante?: boolean;
  cuenta_debe?: string;
  cuenta_haber?: string;
  fecha_inicio_depreciacion?: string;
  precio_adquicicion?: number;
  valor_depreciacion?: number;
  porcentaje_depreciacion?: number;
  meses_depreciacion?: number;
  valor_mensual_depreciacion?: number;
  valor_anual_depreciacion?: number;
  fecha_adquisicion?: string;
  valor_residual?: number;
  fecha_ultima_asignacion?: string;
  fecha_dado_baja?: string;
  observaciones?: string;
  departamento?: number;
  grupo_activo?: string;
  responsable?: string;
};

export type CreateActivosParams = Omit<Activos, 'id_activo'>;
export type UpdateActivosParams = {
  id: string;
  data: Omit<Activos, 'id_activo'>;
};

export const getActivos = async (params?: GetActivosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<ActivosPagingRes>(
    `${urlBackend}api/v1/activo/?${queryParams}`,
    true
  );
};

export const getActivo = async (id: string) => {
  try {
    return await get<Activos>(`${urlBackend}api/v1/activo/${id}/`, true);
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createActivos = async (data: CreateActivosParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Activos>(`${urlBackend}api/v1/activo/`, data, true);
  setAlert(true, res.message, res.status);
  return res;
};

export const updateActivos = async ({ id, data }: UpdateActivosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return put<Activos>(`${urlBackend}api/v1/activo/${id}/`, data, true);
};


export const removeActivos = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Activos>(`${urlBackend}api/v1/activo/${id}/`, true);
  setAlert(true, res.message, res.status);
  return res;
};
